import { useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { GameResultModal } from './summary'
import { ContractDocumentPanel, DiscoveryControls } from './discovery'
import { PackShop } from './shop'
import { ChallengeBoard } from './challenge'
import { LanguageSwitcher } from './components'
import { useContractGameStore } from '../store/useContractGameStore'
import { getContractOutput } from '../engine/contractReducer'

interface ContractGameProps {
  onComplete?: (output: ReturnType<typeof getContractOutput>) => void;
  onExit?: () => void;
}

export function ContractGame({ onComplete, onExit }: ContractGameProps) {
  const { t } = useTranslation()
  const phase = useContractGameStore((s) => s.phase)
  const dispatch = useContractGameStore((s) => s.dispatch)
  const setActiveClauseId = useContractGameStore((s) => s.setActiveClauseId)

  // Define all callbacks BEFORE any early returns to maintain Hook order
  const handleStart = useCallback(() => {
    dispatch({ type: 'START_GAME' })
  }, [dispatch])

  const handleRestart = useCallback(() => {
    dispatch({ type: 'RESTART' })
    setActiveClauseId(null)
  }, [dispatch, setActiveClauseId])

  const handleDebugSkip = useCallback(() => {
    dispatch({ type: 'DEBUG_SKIP_PHASE' })
  }, [dispatch])

  const handleDebugSkipStep = useCallback(() => {
    dispatch({ type: 'DEBUG_SKIP_STEP' })
  }, [dispatch])

  const handleConfirm = useCallback(() => {
    if (onComplete) {
      const state = useContractGameStore.getState()
      onComplete(getContractOutput(state))
    } else {
      handleRestart()
    }
  }, [onComplete, handleRestart])

  // IDLE — full-screen start screen
  if (phase === 'IDLE') {
    const ruleItems = [
      { bold: t('game.rule1Bold'), text: t('game.rule1Text') },
      { bold: t('game.rule2Bold'), text: t('game.rule2Text') },
      { bold: t('game.rule3Bold'), text: t('game.rule3Text') },
    ]
    return (
      <main className="flex flex-col items-center justify-center min-h-full p-4 md:p-10 text-text-primary text-center bg-gradient-page overflow-y-auto"
      >
        <LanguageSwitcher />
        <section
          className="w-full max-w-[760px] glass rounded-xl border border-border-default shadow-[0_12px_36px_rgba(140,120,170,0.16)] px-5 py-7 md:px-10 md:py-10"
          style={{ animation: 'fade-in 500ms ease-out' }}
        >
          <h1
            className="text-3xl md:text-5xl font-black m-0 font-heading text-gradient-rose-gold"
            style={{ animation: 'fade-in 600ms ease-out, slide-up 600ms ease-out' }}
          >
            {t('game.title')}
          </h1>
          <p
            className="text-sm md:text-base text-text-secondary tracking-[2px] md:tracking-[4px] uppercase mt-2 mb-6"
            style={{ animation: 'fade-in 500ms ease-out 100ms both' }}
          >
            {t('game.subtitle')}
          </p>
          <p
            className="max-w-[520px] mx-auto text-[15px] leading-[1.7] text-text-secondary mb-6"
            style={{ animation: 'fade-in 500ms ease-out 200ms both' }}
          >
            {t('game.description')}
          </p>
          <div className="flex flex-col gap-2 mb-8 text-left">
            {ruleItems.map((item, i) => (
              <div
                key={i}
                className="text-[13px] text-text-secondary py-2 px-4 glass rounded-sm shadow-soft"
                style={{ animation: `slide-up 400ms ease-out ${300 + i * 100}ms both` }}
              >
                <strong>{item.bold}</strong>{item.text}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="py-3 px-8 md:py-3.5 md:px-12 bg-gradient-rose-btn border-none rounded-md text-white text-base md:text-lg font-bold cursor-pointer tracking-[2px] shadow-[0_4px_16px_rgba(212,132,154,0.3)]"
            style={{ animation: 'scale-in 300ms ease-out 700ms both, gentle-bounce 2s ease-in-out 1s infinite' }}
            onClick={handleStart}
          >
            {t('game.startButton')}
          </button>
          {onExit && (
            <button
              type="button"
              className="inline-block mt-4 text-sm font-semibold bg-transparent border-none cursor-pointer transition-colors text-text-secondary hover:text-text-primary focus-visible:text-text-primary"
              style={{ animation: 'fade-in 500ms ease-out 800ms both' }}
              onClick={onExit}
            >
              {t('game.backToHub', '返回主界面')}
            </button>
          )}
        </section>
        <DebugSkipButton onClick={handleDebugSkip} />
        <DebugStepButton onClick={handleDebugSkipStep} />
      </main>
    )
  }

  // CONTRACT_DISCOVERY — full-width contract with bottom-right floating controls
  if (phase === 'CONTRACT_DISCOVERY') {
    return (
      <DiscoveryLayout onDebugSkip={handleDebugSkip} onDebugStep={handleDebugSkipStep} />
    )
  }

  // PACK_SHOP — pack shop page
  if (phase === 'PACK_SHOP') {
    return (
      <>
        <PackShop />
        <DebugSkipButton onClick={handleDebugSkip} />
        <DebugStepButton onClick={handleDebugSkipStep} />
      </>
    )
  }

  // CHALLENGE_RESOLUTION — full-width challenge board
  if (phase === 'CHALLENGE_RESOLUTION') {
    return (
      <>
        <ChallengeBoard />
        <DebugSkipButton onClick={handleDebugSkip} />
        <DebugStepButton onClick={handleDebugSkipStep} />
      </>
    )
  }

  // GAME_OVER — keep board visible, overlay result modal
  return (
    <>
      <ChallengeBoard />
      {createPortal(<GameResultModal onConfirm={handleConfirm} />, document.body)}
    </>
  )
}

function DiscoveryLayout({ onDebugSkip, onDebugStep }: { onDebugSkip: () => void; onDebugStep: () => void }) {
  const counterRef = useRef<HTMLSpanElement>(null)
  return (
    <main className="relative h-full overflow-hidden">
      <ContractDocumentPanel fullWidth progressRef={counterRef} />
      <DiscoveryControls counterRef={counterRef} />
      <DebugSkipButton onClick={onDebugSkip} />
      <DebugStepButton onClick={onDebugStep} />
    </main>
  )
}

function DebugSkipButton({ onClick }: { onClick: () => void }) {
  if (import.meta.env.VITE_DEBUG !== 'true') return null
  return (
    <button
      className="fixed bottom-3 left-3 z-50 py-1.5 px-3 bg-red-600/80 hover:bg-red-600 border-none rounded text-white text-[11px] font-mono cursor-pointer backdrop-blur-sm shadow-lg transition-colors"
      onClick={onClick}
      title="Debug: skip to next phase"
    >
      DEBUG SKIP &raquo;
    </button>
  )
}

function DebugStepButton({ onClick }: { onClick: () => void }) {
  const phase = useContractGameStore((s) => s.phase)
  const subPhase = useContractGameStore((s) => s.challengeSubPhase)

  if (import.meta.env.VITE_DEBUG !== 'true') return null

  const label = phase === 'CHALLENGE_RESOLUTION' && subPhase
    ? `STEP [${subPhase}] >`
    : `STEP [${phase}] >`
  return createPortal(
    <button
      className="fixed bottom-10 left-3 z-[201] py-1.5 px-3 bg-amber-600/80 hover:bg-amber-600 border-none rounded text-white text-[11px] font-mono cursor-pointer backdrop-blur-sm shadow-lg transition-colors"
      onClick={onClick}
      title="Debug: advance one step"
    >
      {label}
    </button>,
    document.body,
  )
}
